/**
 * The Providers settings page: one card per provider Forge can install.
 *
 * @module
 */
import { PROVIDER_SETTINGS, PUBLIC_MVP_PROVIDER_SETTINGS } from "./providerSettingsCatalog";
import { buildProviderCards } from "./providerCards.logic";
import { ProviderCard } from "./ProviderCard";
import {
  orgProviderPolicyOrDefault,
  useCmmdOrgProviderPolicy,
} from "../../cmmd/forgeOrgProviderPolicy";
import { cmmdApiClient } from "../../environments/cmmdApi";
import { useClaudeCloudSignIn } from "../../hooks/useClaudeCloudSignIn";
import {
  isCloudProviderSignInEnabled,
  providerAccountsQueryKey,
  useCloudProviderSignIn,
} from "../../hooks/useCloudProviderSignIn";
import { useSettings, useUpdateSettings } from "../../hooks/useSettings";
import { ensureLocalApi } from "../../localApi";
import { resolveAppModelSelectionState } from "../../modelSelection";
import { isProviderSettingsCardVisible, isWalletAllowed } from "../../providerPickerAvailability";
import { applyProvidersUpdated, useServerProviders } from "../../rpc/serverState";
import { Button } from "../ui/button";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { ProviderLastChecked } from "./ProviderStatusIndicators";
import { isDebugDisclosureEnabled } from "./providerProvenance";
import { SettingsPageContainer, SettingsSection } from "./settingsLayout";
import { ProviderKind } from "@cmmd-forge/contracts";
import { DEFAULT_UNIFIED_SETTINGS } from "@cmmd-forge/contracts/settings";
import { useQuery } from "@tanstack/react-query";
import { LoaderIcon, RefreshCwIcon } from "lucide-react";
import { useCallback, useRef, useState } from "react";

// moved verbatim from the 5377-line SettingsPanels.tsx; complexity is
// unchanged, and fallow reads a relocated function as a new one
// fallow-ignore-next-line complexity

// moved verbatim from the 5377-line SettingsPanels.tsx; complexity is
// unchanged, and fallow reads a relocated function as a new one
// fallow-ignore-next-line complexity
export function ProviderSettingsPanel() {
  const settings = useSettings();
  const { updateSettings } = useUpdateSettings();
  const serverProviders = useServerProviders();
  const orgProviderPolicyQuery = useCmmdOrgProviderPolicy(true);
  const orgProviderPolicy = orgProviderPolicyOrDefault(orgProviderPolicyQuery.data);
  const workspaceWalletAllowed = isWalletAllowed("workspace", orgProviderPolicy);
  const codexHomePath = settings.providers.codex.homePath;
  const codexCloudSignIn = useCloudProviderSignIn();
  const { active: claudeCloudActive, controller: claudeCloudSignIn } = useClaudeCloudSignIn();
  // Deliberately NOT gated on the cloud sign-in flags. Linking a second Codex
  // account works on desktop/local surfaces too (startReconnectCodexAuth with
  // credentialTarget "linked" syncs it to CMMD), so gating the inventory on
  // VITE_CMMD_FORGE_AUTH_RUNTIME_ENABLED meant a local user could create a
  // linked account and then never see it — the rows stayed empty, and with them
  // the only Remove action. TanStack does not refetch a disabled query, so even
  // invalidating after a successful link could not surface it.
  //
  // This panel already issues an unconditional CMMD read (useCmmdOrgProviderPolicy
  // above), so this adds no new dependency; retry: false keeps a build with no
  // CMMD reachable from retrying, and the `?? []` below already degrades to the
  // connected-credential fallback (FOR-2605).
  const providerAccountsQuery = useQuery({
    queryKey: providerAccountsQueryKey,
    queryFn: () => cmmdApiClient.getProviderAccounts(),
    staleTime: 30_000,
    retry: false,
  });
  const providerAccounts = providerAccountsQuery.data?.accounts ?? [];
  const codexProviderAccounts = providerAccounts.filter((account) => account.provider === "codex");
  const claudeProviderAccounts = providerAccounts.filter(
    (account) => account.provider === "claudeAgent",
  );
  const hasUsableClaudeProviderAccount = claudeProviderAccounts.some(
    (account) => !account.needsReconnect,
  );
  const codexCloudCredentialConnected =
    isCloudProviderSignInEnabled() &&
    (codexCloudSignIn.connection?.connected === true || codexProviderAccounts.length > 0);
  const claudeSingularNeedsReconnect =
    claudeCloudSignIn.connection?.connected === true &&
    "needsReconnect" in claudeCloudSignIn.connection &&
    claudeCloudSignIn.connection.needsReconnect === true;
  const claudeCloudCredentialConnected =
    claudeCloudActive &&
    ((claudeCloudSignIn.connection?.connected === true && !claudeSingularNeedsReconnect) ||
      hasUsableClaudeProviderAccount);

  const [openProviderDetails, setOpenProviderDetails] = useState<Record<ProviderKind, boolean>>({
    codex: Boolean(
      settings.providers.codex.binaryPath !== DEFAULT_UNIFIED_SETTINGS.providers.codex.binaryPath ||
      settings.providers.codex.homePath !== DEFAULT_UNIFIED_SETTINGS.providers.codex.homePath,
    ),
    claudeAgent: Boolean(
      settings.providers.claudeAgent.binaryPath !==
      DEFAULT_UNIFIED_SETTINGS.providers.claudeAgent.binaryPath,
    ),
    ollama: false,
    cmmd: false,
    apex: false,
    cursor: false,
    opencode: false,
  });
  const [isRefreshingProviders, setIsRefreshingProviders] = useState(false);
  const refreshingRef = useRef(false);
  const refreshProviders = useCallback(() => {
    if (refreshingRef.current) return;
    refreshingRef.current = true;
    setIsRefreshingProviders(true);
    void ensureLocalApi()
      .server.refreshProviders()
      .then((payload) => {
        applyProvidersUpdated(payload);
      })
      .catch((error: unknown) => {
        console.warn("Failed to refresh providers", error);
      })
      .finally(() => {
        refreshingRef.current = false;
        setIsRefreshingProviders(false);
      });
  }, []);

  const textGenerationModelSelection = resolveAppModelSelectionState(settings, serverProviders);
  const textGenProvider = textGenerationModelSelection.provider;

  const visibleProviderSettings = (
    isDebugDisclosureEnabled() ? PROVIDER_SETTINGS : PUBLIC_MVP_PROVIDER_SETTINGS
  ).filter((providerSettings) =>
    isProviderSettingsCardVisible(providerSettings.provider, orgProviderPolicy),
  );

  const providerCards = buildProviderCards({
    visibleProviderSettings,
    serverProviders,
    settings,
    orgProviderPolicy,
    codexProviderAccounts,
    claudeProviderAccounts,
    codexCloudCredentialConnected,
    claudeCloudCredentialConnected,
    codexCloudSignIn,
  });

  const lastCheckedAt =
    serverProviders.length > 0
      ? serverProviders.reduce(
          (latest, provider) => (provider.checkedAt > latest ? provider.checkedAt : latest),
          serverProviders[0]!.checkedAt,
        )
      : null;

  // Hoisted out of the JSX rather than inlined in the map. As an anonymous
  // arrow inside the tree it could not carry the ignore marker below -- a
  // `//` comment placed there is not a comment at all, it is rendered text.
  // It also reads better as a named thing: this is the whole provider card.
  //
  // moved verbatim from the 5377-line SettingsPanels.tsx; complexity is
  // unchanged, and fallow reads a relocated function as a new one
  // fallow-ignore-next-line complexity

  return (
    <SettingsPageContainer>
      <SettingsSection
        title="Providers"
        headerAction={
          <div className="flex items-center gap-1.5">
            <ProviderLastChecked lastCheckedAt={lastCheckedAt} />
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    size="icon-xs"
                    variant="ghost"
                    className="size-5 rounded-sm p-0 text-muted-foreground hover:text-foreground"
                    disabled={isRefreshingProviders}
                    onClick={() => void refreshProviders()}
                    aria-label="Refresh provider status"
                  >
                    {isRefreshingProviders ? (
                      <LoaderIcon className="size-3 animate-spin" />
                    ) : (
                      <RefreshCwIcon className="size-3" />
                    )}
                  </Button>
                }
              />
              <TooltipPopup side="top">Refresh provider status</TooltipPopup>
            </Tooltip>
          </div>
        }
      >
        {providerCards.map((providerCard) => (
          <ProviderCard
            claudeCloudActive={claudeCloudActive}
            claudeCloudSignIn={claudeCloudSignIn}
            claudeProviderAccounts={claudeProviderAccounts}
            codexCloudSignIn={codexCloudSignIn}
            codexHomePath={codexHomePath}
            codexProviderAccounts={codexProviderAccounts}
            key={providerCard.provider}
            openProviderDetails={openProviderDetails}
            providerCard={providerCard}
            refreshProviders={refreshProviders}
            setOpenProviderDetails={setOpenProviderDetails}
            settings={settings}
            textGenProvider={textGenProvider}
            updateSettings={updateSettings}
            workspaceWalletAllowed={workspaceWalletAllowed}
          />
        ))}
      </SettingsSection>
    </SettingsPageContainer>
  );
}
